import { Box, Card, Stack, TypographyProps } from '@mui/material';
import ArticleCoverImage from '../Article/ArticleCoverImage';
import ArticleMeta from '../../types/ArticleMeta';
import OverlineSection from './OverlineSection';
import MainContentArea from './MainContentArea';
import ArticleCardFooter from './ArticleCardFooter';
import {
  articleCardClickTarget,
  sendArticleCardClickToGTM,
} from '../../utilities/gtm';
import { CardGTMData } from './CardUtils';

interface HighlightedArticleCardProps {
  readonly meta: ArticleMeta;
  readonly heading: TypographyProps['component'];
  readonly gtmData?: CardGTMData;
  readonly hidePrimaryCategory?: boolean;
  readonly hideCategories?: boolean;
  readonly showOnlyCustomCoverImage?: boolean;
  readonly hideCoverImage?: boolean;
  readonly imageAspectRatio?: number;
}
export default function HighlightedArticleCard({
  meta,
  heading = 'h2',
  gtmData = {},
  hidePrimaryCategory = false,
  hideCategories = false,
  showOnlyCustomCoverImage = false,
  hideCoverImage = false,
  imageAspectRatio,
}: HighlightedArticleCardProps) {
  return (
    <Card
      component='article'
      sx={{
        paddingX: 2,
        paddingBottom: 0.25,
        paddingTop: hideCoverImage ? 2 : 0,
        height: '100%',
      }}
      variant='outlined'
    >
      {hideCoverImage === false &&
        (showOnlyCustomCoverImage === false || meta.hasCoverImage()) && (
          <Box
            sx={{
              marginX: -2,
              marginBottom: 1.5,
            }}
          >
            <ArticleCoverImage
              src={meta.coverImage}
              category={
                /* Only define the category if default cover image is shown. If 
                only custom cover image is shown, the category shoulod not be
                defined so that the default category image won't be shown as a
                fallback option. */
                showOnlyCustomCoverImage ? undefined : meta.categories.primary
              }
              aspectRatio={imageAspectRatio}
              isVideo={meta.hasVideoContent()}
            />
          </Box>
        )}

      <Stack
        direction='row'
        useFlexGap
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 2,
          marginBottom: 1,
        }}
      >
        <OverlineSection type={meta.type} openContent={meta.isPublic} />
      </Stack>

      <MainContentArea
        meta={meta}
        heading={heading}
        variant='highlight'
        hideDescription
        onTitleClick={() => {
          sendArticleCardClickToGTM(
            meta,
            articleCardClickTarget.ARTICLE,
            gtmData.location,
            gtmData.index,
          );
        }}
      />

      <ArticleCardFooter
        meta={meta}
        variant='highlight'
        hidePrimaryCategory={hidePrimaryCategory}
        hideCategories={hideCategories}
        onAuthorClick={() => {
          sendArticleCardClickToGTM(
            meta,
            articleCardClickTarget.AUTHOR,
            gtmData.location,
            gtmData.index,
          );
        }}
        onCategoryClick={() => {
          sendArticleCardClickToGTM(
            meta,
            articleCardClickTarget.CATEGORY,
            gtmData.location,
            gtmData.index,
          );
        }}
      />
    </Card>
  );
}
